/** Catálogo das áreas do Hub (24/10/2026): Hub → Áreas → Módulos → Telas.
 *
 *  Espelha `backend/app/areas.py`, que é quem decide o acesso. Aqui mora o que a
 *  interface precisa: rótulo, endereço, ícone e a navegação de cada área. A ordem é
 *  a dos cards da tela de áreas e do seletor, com o Administrativo sempre por último
 *  (Amorim, 24/10/2026).
 *
 *  Área nova: entrada aqui, a pasta `src/areas/<chave>/` com nav e rotas, e a mesma
 *  chave no backend. O roteiro completo está em docs/areas/05-como-adicionar.md. */
import type { NavSection } from '../router/navConfig'
import { logisticaNav } from '../../areas/logistica/nav'
import { administrativoNav } from '../../areas/administrativo/nav'
import { dpNav } from '../../areas/dp/nav'
import { abastecimentoNav } from '../../areas/abastecimento/nav'
import { ARTE_DAS_AREAS, type NoDaArte } from './arte'

export type AreaKey = 'logistica' | 'administrativo' | 'dp' | 'abastecimento'

export interface AreaDef {
  key: AreaKey
  /** Rótulo em PT, para quando a tradução faltar. */
  label: string
  labelKey: string
  descricaoKey: string
  /** Raiz das rotas da área. Toda tela da área mora debaixo dela. */
  basePath: string
  /** O ícone da área, no traço do Lucide. O mesmo desenho serve de ícone pequeno no
   *  seletor e de arte grande no card. */
  arte: readonly NoDaArte[]
  nav: NavSection[]
}

export const AREAS: readonly AreaDef[] = [
  {
    key: 'logistica',
    label: 'Logística',
    labelKey: 'areas.logistica.nome',
    descricaoKey: 'areas.logistica.descricao',
    basePath: '/logistica',
    arte: ARTE_DAS_AREAS.logistica,
    nav: logisticaNav,
  },
  {
    key: 'dp',
    label: 'Departamento Pessoal',
    labelKey: 'areas.dp.nome',
    descricaoKey: 'areas.dp.descricao',
    basePath: '/dp',
    arte: ARTE_DAS_AREAS.dp,
    nav: dpNav,
  },
  {
    key: 'abastecimento',
    label: 'Abastecimento',
    labelKey: 'areas.abastecimento.nome',
    descricaoKey: 'areas.abastecimento.descricao',
    basePath: '/abastecimento',
    arte: ARTE_DAS_AREAS.abastecimento,
    nav: abastecimentoNav,
  },
  {
    key: 'administrativo',
    label: 'Administrativo',
    labelKey: 'areas.administrativo.nome',
    descricaoKey: 'areas.administrativo.descricao',
    basePath: '/administrativo',
    arte: ARTE_DAS_AREAS.administrativo,
    nav: administrativoNav,
  },
]

export const AREA_KEYS: readonly AreaKey[] = AREAS.map((a) => a.key)

/** Tela de escolha de área, a entrada de quem tem mais de uma. */
export const AREAS_PATH = '/areas'

export function areaPorChave(key: AreaKey): AreaDef {
  const area = AREAS.find((a) => a.key === key)
  if (!area) throw new Error(`área desconhecida: ${key}`)
  return area
}

export function ehAreaKey(valor: unknown): valor is AreaKey {
  return typeof valor === 'string' && (AREA_KEYS as readonly string[]).includes(valor)
}

/** A área dona de um endereço, pela raiz. Rota fora das áreas, como `/conta/*` e
 *  o portal, não tem área. */
export function areaDoCaminho(pathname: string): AreaKey | null {
  const area = AREAS.find((a) => pathname === a.basePath || pathname.startsWith(`${a.basePath}/`))
  return area ? area.key : null
}
